import { fetchAuthSession } from "aws-amplify/auth";

const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOpts {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

async function rawRequest(path: string, opts: RequestOpts = {}): Promise<unknown> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(await authHeader()),
  };
  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string } })?.error ?? {};
    throw new ApiError(e.code ?? "http_error", e.message ?? `HTTP ${res.status}`, res.status);
  }
  return json;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const json = (await rawRequest(path, opts)) as { data: T };
  return json.data;
}

export const api = {
  get: <T,>(path: string, opts?: RequestOpts) => request<T>(path, { ...opts, method: "GET" }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  /** Returns the full response envelope (data + pagination + ...) without
   *  unwrapping `data`. Use for paginated endpoints. */
  getRaw: <T,>(path: string, opts?: RequestOpts) => rawRequest(path, { ...opts, method: "GET" }) as Promise<T>,
};
